import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { eventAPI } from '@/services/api';
import { CalendarDays, LayoutGrid, Calendar as CalendarIcon, MapPin, X, SlidersHorizontal } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import MapFilter from '../components/MapFilter';
import SaveSearchButton from '../components/SaveSearchButton';
import FilterGroup from "@/components/FilterGroup";
import SearchResultCard from "@/components/SearchResultCard";
import PaginationCard from "@/components/PaginationCard";
import { handleUpdateFilter } from "@/utils/filterUtil";
import EventCalendar from '@/components/EventCalendar';
import { Button } from "@/components/ui/button";
import { injectAds } from "@/utils/adUtil";
import AdSlot from "@/components/AdSlot";


const EVENT_TYPES = ['competition', 'clinic', 'show', 'auction', 'meetup', 'race', 'course', 'other'];
const DISCIPLINES = ['dressage', 'show_jumping', 'vaulting', 'trail', 'western', 'classical', 'therapy',
	'natural_horsemanship', 'liberty', 'groundwork', 'colt_starting', 'young_horse', 'sports_therapy'];
const LEVELS = ['beginner', 'intermediate', 'advanced', 'competition'];

export default function EventListingPage() {
	const { t, i18n } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [items, setItems] = useState([]);
	const [total, setTotal] = useState(0);
	const [pages, setPages] = useState(1);
	const [loading, setLoading] = useState(true);
	const [geoActive, setGeoActive] = useState(false);
	const [pin, setPin] = useState(null);
	const [mapSearchOpen, setMapSearchOpen] = useState(false);
	const [radius, setRadius] = useState(25);
	const [view, setView] = useState(searchParams.get('view') === 'calendar' ? 'calendar' : 'grid');
	const [calendarItems, setCalendarItems] = useState([]);
	const [calendarLoading, setCalendarLoading] = useState(false)
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState({
		event_type: searchParams.get('event_type') || '',
		discipline: searchParams.get('discipline') || '',
		level: searchParams.get('level') || '',
		is_free: searchParams.get('is_free') || '',
		date_from: searchParams.get('date_from') || '',
		date_to: searchParams.get('date_to') || '',
		min_price: searchParams.get('min_price') || '',
		max_price: searchParams.get('max_price') || '',
		location: searchParams.get('location') || '',
		lat: searchParams.get('lat') || '',
		lng: searchParams.get('lng') || '',
		radius: searchParams.get('radius') || '',
		include_past: searchParams.get('include_past') || '',
		verified_only: searchParams.get('verified_only') || '',
		sort: searchParams.get('sort') || 'upcoming',
		page: parseInt(searchParams.get('page') || '1'),
	});

	useEffect(() => {
		setLoading(true);
		const params = {};
		Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) params[k] = v; });
		eventAPI.list(params).then((r) => {
			setItems(injectAds(r.data.items || [], filters.page));
			setTotal(r.data.total || 0);
			setPages(r.data.pages || 1);
		}).catch(() => {}).finally(() => setLoading(false));
	}, [filters]);

	// Fetch a wider set for the calendar view (ignore date filters + pagination so users can navigate freely)
	useEffect(() => {
		if (view !== 'calendar') return;
		setCalendarLoading(true);
		const params = { limit: 200 };
		['event_type', 'discipline', 'level', 'is_free', 'location'].forEach((k) => {
			if (filters[k]) params[k] = filters[k];
		});
		if (filters.include_past === 'true') {
			params.include_past = true;
		}
		eventAPI
			.list(params)
			.then((r) => setCalendarItems(r.data.items || []))
			.catch(() => {})
			.finally(() => setCalendarLoading(false));
	}, [view, filters.event_type, filters.discipline, filters.level, filters.is_free, filters.location, filters.include_past]);

	const updateFilter = (key, value) =>
		handleUpdateFilter(key, value, filters, setFilters, setSearchParams);

	const clearFilters = () => {
		const cleared = {
			event_type: '',
			discipline: '',
			level: '',
			is_free: '',
			date_from: '',
			date_to: '',
			min_price: '',
			max_price: '',
			location: '',
			lat: '',
			lng: '',
			radius: '',
			include_past: '',
			verified_only: '',
			sort: 'upcoming',
			page: '1'
		};
		setPin(null);
		setFilters(cleared);
		setGeoActive(false);
		setSearchParams({});
	};

	const changePage = (p) => updateFilter('page', p);

	const hasActiveFilters = filters.discipline || filters.event_type || filters.level ||
		filters.is_free || filters.date_from || filters.date_to || filters.min_price || filters.max_price
		|| filters.include_past || filters.verified_only || geoActive || pin;

	return (
		<div className="pt-20 md:pt-24 min-h-screen" data-testid="event-listing-page">

			{/* Header */}
			<div className="bg-white border-b border-[#E6E2D6]">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
					<div className="flex items-center gap-3 mb-2">
						<CalendarDays className="w-7 h-7 text-[#8B4513]" />
						<h1 className="text-3xl md:text-4xl font-medium text-[#1A3C34] font-['Playfair_Display']">
							{t('event.title')}
						</h1>
					</div>
					<p className="text-[#666055]">{total} {t('common.results')}</p>
				</div>
			</div>

			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<div className="flex gap-8">
					{/* Sidebar Filters - Desktop */}
					<aside className="hidden lg:block w-72 shrink-0">
						<div className="sticky top-24 space-y-6">
							<h3 className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display']">
								{t('horse.filters')}
							</h3>
							{ hasActiveFilters && (
								<button onClick={ clearFilters } className="text-xs text-[#8B4513] hover:underline"
												data-testid="clear-filters">
									{ t('horse.clear_filters') }
								</button>
							) }

							<FilterGroup label={ t('event.event_type') }>
								<Select value={filters.event_type || 'all'}
												onValueChange={(v) => updateFilter('event_type', v === 'all' ? '' : v)}>
									<SelectTrigger data-testid="filter-event-type"
																 className="bg-white border-[#E6E2D6] rounded-sm h-10">
										<SelectValue placeholder={t('common.all')} />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">{t('common.all')}</SelectItem>
										{ EVENT_TYPES.map(u =>
											<SelectItem key={u} value={u}>{t(`event.type.${u}`)}</SelectItem>)
										}
									</SelectContent>
								</Select>
							</FilterGroup>

							<FilterGroup label={ t('school.discipline') }>
								<Select value={filters.discipline || 'all'}
												onValueChange={(v) => updateFilter('discipline', v === 'all' ? '' : v)}>
									<SelectTrigger data-testid="filter-boarding-type"
																 className="bg-white border-[#E6E2D6] rounded-sm h-10">
										<SelectValue placeholder={t('common.all')} />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">{t('common.all')}</SelectItem>
										{DISCIPLINES.map(b =>
											<SelectItem key={b} value={b}>{t(`trainer.discipline_opt.${b}`)}</SelectItem>
										)}
									</SelectContent>
								</Select>
							</FilterGroup>

							<FilterGroup label={ t('event.level') }>
								<Select value={filters.level || 'all'}
												onValueChange={(v) => updateFilter('level', v === 'all' ? '' : v)}>
									<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-10">
										<SelectValue placeholder={t('common.all')} />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="all">{t('common.all')}</SelectItem>
										{ LEVELS.map(l =>
											<SelectItem key={l} value={l}>{t(`event.level_opt.${l}`)}</SelectItem>)
										}
									</SelectContent>
								</Select>
							</FilterGroup>

							<FilterGroup label={ t('event.date_range') }>
								<Input type="date" value={filters.date_from}
											 onChange={e => updateFilter('date_from', e.target.value)}
											 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm"
											 data-testid="filter-date-from" />
								<Input type="date" value={filters.date_to}
											 onChange={e => updateFilter('date_to', e.target.value)}
											 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm"
											 data-testid="filter-date-to" />
							</FilterGroup>

							<div>
								<label className="flex items-center gap-2 text-sm text-[#3a3a3a] cursor-pointer">
									<input type="checkbox" checked={filters.is_free === 'true'}
												 onChange={e => updateFilter('is_free', e.target.checked ? 'true' : '')}
												 data-testid="filter-free-only" />
									{t('event.free_only')}
								</label>
							</div>

							<FilterGroup label={ t('horse.price') }>
								<div className="flex gap-2">
									<Input type="number" placeholder="Min" value={filters.min_price}
												 onChange={e => updateFilter('min_price', e.target.value)}
												 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm" />
									<Input type="number" placeholder="Max" value={filters.max_price}
												 onChange={e => updateFilter('max_price', e.target.value)}
												 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm" />
								</div>
							</FilterGroup>

							<FilterGroup label={t('horse.location')}>
								<Input data-testid="filter-location" placeholder={t('horse.location')} value={filters.location}
											 onChange={e => updateFilter('location', e.target.value)}
											 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm" />
							</FilterGroup>

							<div>
								<label className="flex items-center gap-2 text-sm text-[#3a3a3a] cursor-pointer">
									<input type="checkbox" checked={filters.include_past === 'true'}
												 onChange={e => updateFilter('include_past', e.target.checked ? 'true' : '')}
												 data-testid="filter-include-past" />
									{t('event.show_past')}
								</label>
							</div>
							<div>
								<label className="flex items-center gap-2 text-sm text-[#3a3a3a] cursor-pointer">
									<input type="checkbox" checked={filters.verified_only === 'true'}
												 onChange={e => updateFilter('verified_only', e.target.checked ? 'true' : '')}
												 data-testid="filter-mobile" />
									{t('common.only_verified_sellers')}
								</label>
							</div>
						</div>
					</aside>

					<div className="flex-1">
						<div className="flex justify-between items-center mb-6 flex-wrap gap-2">
							<div className="inline-flex items-center border border-[#E6E2D6] rounded-sm overflow-hidden bg-white"
									 data-testid="event-view-toggle">
								<button
									onClick={() => {
										setView('grid');
										const sp = new URLSearchParams(searchParams);
										sp.delete('view');
										setSearchParams(sp);
									}}
									className={`px-3 py-2 text-xs font-medium inline-flex items-center gap-1.5 transition-colors 
											${view === 'grid' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:bg-[#F2F0E9]'}`}
									data-testid="event-view-grid"
								>
									<LayoutGrid className="w-3.5 h-3.5" /> {t('event.view_grid')}
								</button>
								<button
									onClick={() => {
										setView('calendar');
										const sp = new URLSearchParams(searchParams);
										sp.set('view', 'calendar');
										setSearchParams(sp);
									}}
									className={`px-3 py-2 text-xs font-medium inline-flex items-center gap-1.5 transition-colors 
											${view === 'calendar' ? 'bg-[#1A3C34] text-white' : 'text-[#666055] hover:bg-[#F2F0E9]'}`}
									data-testid="event-view-calendar"
								>
									<CalendarIcon className="w-3.5 h-3.5" /> {t('event.view_calendar')}
								</button>
							</div>
							{view === 'grid' && (
								<div className="flex items-center justify-end mb-6 gap-4">
									<button
										onClick={() => setShowFilters(true)}
										className="lg:hidden flex items-center gap-2 px-4 py-2 border border-[#E6E2D6] rounded-sm text-sm
                		text-[#1A3C34] hover:bg-[#F2F0E9]"
										data-testid="mobile-filter-toggle"
									>
										<SlidersHorizontal className="w-4 h-4" /> {t('horse.filters')}
									</button>
									<SaveSearchButton adType="event" />
									<Select value={filters.sort} onValueChange={(v) => updateFilter('sort', v)}>
										<SelectTrigger className="w-48 bg-white border-[#E6E2D6] rounded-sm h-10">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="upcoming">{t('event.sort_upcoming')}</SelectItem>
											<SelectItem value="newest">{t('horse.sort_newest')}</SelectItem>
											<SelectItem value="price_asc">{t('horse.sort_price_asc')}</SelectItem>
											<SelectItem value="price_desc">{t('horse.sort_price_desc')}</SelectItem>
											{ geoActive && <SelectItem value="distance">{t('search.distance')}</SelectItem>}
										</SelectContent>
									</Select>
								</div>
							)}
						</div>

						<div className="flex items-start justify-between gap-3 mb-2">
							<div className="flex-1">
								<MapFilter
									pin={ pin }
									setPin={ setPin }
									radius={ radius}
									setRadius={ setRadius }
									open={ mapSearchOpen }
									setOpen={ setMapSearchOpen }
									geoActive={ geoActive }
									setGeoActive={ setGeoActive }
									onSearch={() => {
										const newFilters = { ...filters, lat: String(pin.lat), lng: String(pin.lng), radius: String(radius), sort: 'distance', page: 1 };
										setFilters(newFilters);
										const sp = new URLSearchParams();
										Object.entries(newFilters).forEach(([k, v]) => { if (v && v !== '' && v !== 1) sp.set(k, v); });
										setSearchParams(sp);
									}}
								/>
							</div>
						</div>

						<div className="flex md:hidden items-start justify-between mb-2">
							<SaveSearchButton adType="event" isMobile={ true }/>
						</div>

						{view === 'calendar' ? (
							<>
								{calendarLoading ? (
									<div className="bg-white border border-[#E6E2D6] rounded-sm p-16 text-center text-sm text-[#666055]">
										{t('common.loading')}
									</div>
								) : (
									<EventCalendar
										events={calendarItems}
										selectedDate={filters.date_from && filters.date_from === filters.date_to ? filters.date_from : null}
										onDayClick={(iso) => {
											setFilters((p) => {
												const same = p.date_from === iso && p.date_to === iso;
												return { ...p, date_from: same ? '' : iso, date_to: same ? '' : iso, page: 1 };
											});
										}}
									/>
								)}
								{filters.date_from && filters.date_from === filters.date_to && (
									<div className="mt-6" data-testid="event-day-picked">
										<div className="flex items-center justify-between mb-3 flex-wrap gap-2">
											<h3 className="text-lg font-medium text-[#1A3C34] font-['Playfair_Display']">
												{t('event.events_on', { date: new Date(filters.date_from + 'T00:00:00')
														.toLocaleDateString((i18n.language || 'en')
															.split('@')[0], { day: '2-digit', month: 'long', year: 'numeric' }) })}
											</h3>
											<button onClick={() => setFilters((p) => ({ ...p, date_from: '', date_to: '' }))}
															className="text-xs text-[#8B4513] hover:underline" data-testid="event-clear-day">
												{t('event.clear_selection')}
											</button>
										</div>
										<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
											{items.length === 0 && !loading && (
												<div className="col-span-full text-center py-8 text-sm text-[#666055]">{t('event.no_results')}</div>
											)}
											{items.map(it => (
												<Link
													key={it.id}
													to={`/events/${it.slug || it.id}`}
													state={{ from: { path: `/events${ view === 'calendar' ? '?view=calendar' : ''} `, label: t('nav.events') } }}
													className="horse-card group bg-white border border-[#E6E2D6] rounded-sm overflow-hidden
															flex flex-col"
													data-testid={`event-day-item-${it.id}`}
												>
													<div className="p-4">
														<span className="text-[10px] font-bold bg-[#F2F0E9] text-[#1A3C34] px-2 py-0.5
																rounded-sm uppercase tracking-wider">
															{t(`event.type.${it.event_type || 'other'}`)}
														</span>
														<h4 className="text-base font-semibold text-[#1A3C34] group-hover:text-[#8B4513] mt-2
																font-['Playfair_Display']">
															{it.title}
														</h4>
														<div className="flex items-baseline gap-1 mt-2">
															{it.is_free ? <p className="text-xl font-bold text-[#1A3C34]">{t('event.free')}</p> :
																<p className="text-xl font-bold text-[#8B4513]">&euro;{it.price?.toLocaleString()}</p>}
														</div>
														{it.location_name && (
															<div className="flex items-center gap-1 mt-2 text-xs text-[#666055]/70">
																<MapPin className="w-3 h-3" />{it.location_name}
															</div>
														)}
													</div>
												</Link>
											))}
										</div>
									</div>
								)}
							</>
						) : loading ? (
							<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
								{[...Array(6)].map((_, i) => (
									<div key={i} className="bg-white rounded-sm border border-[#E6E2D6] overflow-hidden">
										<div className="aspect-[4/3] img-skeleton" />
										<div className="p-5 space-y-3">
											<div className="h-5 w-3/4 img-skeleton rounded" />
											<div className="h-7 w-1/3 img-skeleton rounded" />
										</div>
									</div>
								))}
							</div>
						) : items.length === 0 ? (
							<div className="text-center py-20" data-testid="no-events">
								<CalendarDays className="w-12 h-12 mx-auto mb-4 text-[#D4C5A9]" />
								<p className="text-lg text-[#666055]">{t('event.no_results')}</p>
							</div>
						) : (
							<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="event-grid">
								{items.map((it, i) =>
									it.type === 'listing' ? (
										<SearchResultCard key={i} ad={ it } adType='event' uri="events" />
									) : (
										<AdSlot
											key={it.id}
											id={it.id}
											provider="gam"
											placement="search_results_infeed"
										/>
									)
								)}
							</div>
						)}

						{pages > 1 && (
							<PaginationCard pages={pages} page={ filters.page } changePage={ changePage } />
						)}
					</div>
				</div>
			</div>

			{/* Mobile Filters Drawer */}
			{showFilters && (
				<div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-filters-drawer">
					<div className="absolute inset-0 bg-black/40" onClick={() => setShowFilters(false)} />
					<div className="absolute right-0 top-0 bottom-0 w-80 bg-white p-6 overflow-auto animate-slide-in">
						<div className="flex items-center justify-between mb-6">
							<h3 className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display']">
								{t('horse.filters')}
							</h3>
							<button onClick={() => setShowFilters(false)}>
								<X className="w-5 h-5" />
							</button>
						</div>
						<div className="space-y-4">
							<Select value={filters.event_type || 'all'}
											onValueChange={(v) => updateFilter('event_type', v === 'all' ? '' : v)}>
								<SelectTrigger data-testid="filter-event-type"
															 className="bg-white border-[#E6E2D6] rounded-sm h-10">
									<SelectValue placeholder={t('event.all_event_types')} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">{t('event.all_event_types')}</SelectItem>
									{ EVENT_TYPES.map(u =>
										<SelectItem key={u} value={u}>{t(`event.type.${u}`)}</SelectItem>)
									}
								</SelectContent>
							</Select>
							<Select value={filters.discipline || 'all'}
											onValueChange={(v) => updateFilter('discipline', v === 'all' ? '' : v)}>
								<SelectTrigger data-testid="filter-boarding-type"
															 className="bg-white border-[#E6E2D6] rounded-sm h-10">
									<SelectValue placeholder={t('event.all_disciplines')} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">{t('event.all_disciplines')}</SelectItem>
									{DISCIPLINES.map(b =>
										<SelectItem key={b} value={b}>{t(`trainer.discipline_opt.${b}`)}</SelectItem>
									)}
								</SelectContent>
							</Select>
							<Select value={filters.level || 'all'}
											onValueChange={(v) => updateFilter('level', v === 'all' ? '' : v)}>
								<SelectTrigger className="bg-white border-[#E6E2D6] rounded-sm h-10">
									<SelectValue placeholder={t('event.all_levels')} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">{t('event.all_levels')}</SelectItem>
									{ LEVELS.map(l =>
										<SelectItem key={l} value={l}>{t(`event.level_opt.${l}`)}</SelectItem>)
									}
								</SelectContent>
							</Select>
							<div className="flex flex-col gap-1">
								<label className="text-xs text-[#666055]">{t('event.start_date')}</label>
								<Input type="date" value={filters.date_from}
											 onChange={e => updateFilter('date_from', e.target.value)}
											 placeholder={t('event.start_date')}
											 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm"
											 data-testid="filter-date-from" />
							</div>
							<div className="flex flex-col gap-1">
								<label className="text-xs text-[#666055]">{t('event.end_date')}</label>
								<Input type="date" value={filters.date_to}
											 onChange={e => updateFilter('date_to', e.target.value)}
											 placeholder={t('event.end_date')}
											 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm"
											 data-testid="filter-date-to" />
							</div>
							<label className="flex items-center gap-2 text-sm text-[#3a3a3a] cursor-pointer">
								<input type="checkbox" checked={filters.is_free === 'true'}
											 onChange={e => updateFilter('is_free', e.target.checked ? 'true' : '')}
											 data-testid="filter-free-only" />
								{t('event.free_only')}
							</label>
							<div className="flex gap-2">
								<Input data-testid="filter-min-price" type="number" placeholder={t('horse.min_price')}
											 value={filters.min_price} onChange={e => updateFilter('min_price', e.target.value)}
											 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm" />
								<Input data-testid="filter-max-price" type="number" placeholder={t('horse.max_price')}
											 value={filters.max_price} onChange={e => updateFilter('max_price', e.target.value)}
											 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm" />
							</div>
							<Input data-testid="filter-location" placeholder={t('horse.location')} value={filters.location}
										 onChange={e => updateFilter('location', e.target.value)}
										 className="bg-white border-[#E6E2D6] rounded-sm h-10 text-sm" />
							<label className="flex items-center gap-2 text-sm text-[#3a3a3a] cursor-pointer">
								<input type="checkbox" checked={filters.include_past === 'true'}
											 onChange={e => updateFilter('include_past', e.target.checked ? 'true' : '')}
											 data-testid="filter-include-past" />
								{t('event.show_past')}
							</label>
							<label className="flex items-center gap-2 text-sm text-[#3a3a3a] cursor-pointer">
								<input type="checkbox" checked={filters.verified_only === 'true'}
											 onChange={e => updateFilter('verified_only', e.target.checked ? 'true' : '')}
											 data-testid="filter-mobile" />
								{t('common.only_verified_sellers')}
							</label>
							<Button onClick={() => setShowFilters(false)}
											className="w-full bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm h-10">
								{t('search.apply')}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}